import axios from "axios"

import http from "@/core/global/http"
import { normalizeScannerPatientListResponse } from "@/core/model/integrations"

import type {
  ConnectScannerPayload,
  ConnectScannerResponse,
  PatientLoadTriggerResponse,
  RefreshPatientsResponse,
  ScannerFilesLoadResponse,
  ScannerFilesMode,
  ScannerPatientListQueryParams,
} from "./integrations.type"
import { buildScannerPatientListBody } from "./integrations.util"
import {
  ALL_SCANNER_INTEGRATION_IDS,
  formatScannerHeaderValue,
} from "./scannerConstants"

// Auth based endpoints

export const getScannerRedirectUrlApi = async (
  org_id: string,
  scanner: string,
) => {
  const { data } = await http.get(
    `/shared/v1/org/${org_id}/scanner/redirect-url`,
    { headers: { Scanner: formatScannerHeaderValue(scanner) } },
  )
  if (data?.status?.toLowerCase() === "success" && data.result?.redirect_url) {
    return data.result.redirect_url
  }
  throw new Error(data.message?.message ?? "Failed to get redirect URL")
}

export const getScannerConnectionStatusApi = async (org_id: string) => {
  const { data } = await http.get(
    `/shared/v1/org/${org_id}/scanner/connection-status`,
    {
      headers: {
        Scanner: formatScannerHeaderValue(ALL_SCANNER_INTEGRATION_IDS),
      },
    },
  )

  if (data?.status?.toLowerCase() === "success" && data.result) {
    return {
      org_id: data.result.org_id,
      connections: data.result.connections ?? [],
    }
  }
  throw new Error(data.message?.message ?? "Failed to get connection status")
}

export const connectScannerApi = async (
  org_id: string,
  scanner: string,
  payload: ConnectScannerPayload,
): Promise<string> => {
  const response = await http.post(
    `/shared/v1/org/${org_id}/scanner/connect`,
    payload,
    { headers: { Scanner: formatScannerHeaderValue(scanner) } },
  )
  // Cast rather than destructure: http.post's generic doubles as the request body's
  // type, so passing a typed payload makes TS infer the response as that same type.
  const data = response.data as {
    status?: string
    result?: ConnectScannerResponse
    message?: { message?: string }
  }
  if (data?.status?.toLowerCase() === "success" && data.result?.connection_id) {
    return data.result.connection_id
  }
  throw new Error(data?.message?.message ?? "Failed to connect scanner")
}

const buildScannerHeaders = (
  scannerName: string,
  connectionId?: string,
): Record<string, string> => {
  const headers: Record<string, string> = {
    Scanner: formatScannerHeaderValue(scannerName),
  }
  if (connectionId) {
    headers["connection-id"] = connectionId
  }
  return headers
}

/** `connection-id` is required for scanners that support multiple simultaneous
 * connections (iTero) so the backend knows which specific account to disconnect. */
export const disconnectScannerApi = async (
  org_id: string,
  scanner: string,
  connectionId?: string,
) => {
  const { data } = await http.post(
    `/shared/v1/org/${org_id}/scanner/disconnect`,
    undefined,
    { headers: buildScannerHeaders(scanner, connectionId) },
  )

  if (
    data?.status?.toLowerCase() === "success" &&
    data.result?.disconnected === true
  ) {
    return true
  }
  throw new Error(data.message?.message ?? "Failed to disconnect")
}

// Scanner patient list endpoints

export const triggerPatientListFetchApi = async (
  org_id: string,
  scannerName: string,
  connectionId?: string,
): Promise<PatientLoadTriggerResponse> => {
  const { data } = await http.post(
    `/shared/v1/org/${org_id}/scanner/patients/load`,
    undefined,
    { headers: buildScannerHeaders(scannerName, connectionId) },
  )
  if (data?.status?.toLowerCase() === "success") {
    return (data.result ?? {}) as PatientLoadTriggerResponse
  }
  throw new Error(
    data?.message?.message ?? "Failed to trigger patient list fetch",
  )
}

export const fetchScannerPatientListApi = async (
  org_id: string,
  params: ScannerPatientListQueryParams,
  scannerName: string,
  connectionId?: string,
) => {
  const { data } = await http.post(
    `/shared/v1/org/${org_id}/scanner/patients`,
    buildScannerPatientListBody(params),
    { headers: buildScannerHeaders(scannerName, connectionId) },
  )
  return normalizeScannerPatientListResponse(data)
}

export const refreshScannerPatientsApi = async (
  org_id: string,
  scannerName: string,
  connectionId?: string,
): Promise<RefreshPatientsResponse> => {
  const { data } = await http.post(
    `/shared/v1/org/${org_id}/scanner/patients/refresh`,
    undefined,
    { headers: buildScannerHeaders(scannerName, connectionId) },
  )
  if (data?.status?.toLowerCase() === "success" && data.result) {
    return data.result as RefreshPatientsResponse
  }
  throw new Error(data?.message?.message ?? "Failed to refresh patients")
}

// Scanner attachment endpoints (case-level) — legacy

/** @deprecated Use `loadScannerFilesApi` instead */
export const fetchScannerAttachments = (
  org_id: string,
  caseId: string | undefined,
  scannerName: string,
) => {
  return http.get(`/shared/v1/org/${org_id}/scanner/${caseId}/attachments`, {
    headers: { Scanner: formatScannerHeaderValue(scannerName) },
  })
}

/** @deprecated Use `downloadScannerFileFromUrlApi` instead */
export const downloadScannerAttachment = async (
  org_id: string,
  caseId: string | undefined,
  attachmentId: number,
  fileName: string,
  scannerName: string,
) => {
  const res = await http.get(
    `/shared/v1/org/${org_id}/scanner/${caseId}/attachments/${attachmentId}`,
    {
      responseType: "blob",
      headers: { Scanner: formatScannerHeaderValue(scannerName) },
    },
  )
  return new File([res.data], fileName)
}

// New scanner /files/load endpoints

/** POST /shared/v1/org/{org_id}/scanner/files/load */
export const loadScannerFilesApi = async (
  org_id: string,
  externalCaseId: string | number,
  scannerName: string,
  mode: ScannerFilesMode = "temporary",
  connectionId?: string,
): Promise<ScannerFilesLoadResponse> => {
  const response = await http.post(
    `/shared/v1/org/${org_id}/scanner/files/load`,
    { external_case_id: String(externalCaseId), mode },
    { headers: buildScannerHeaders(scannerName, connectionId) },
  )
  const data = response.data as {
    status?: string
    result?: ScannerFilesLoadResponse
    message?: { message?: string }
  }
  if (data?.status?.toLowerCase() === "success" && data.result) {
    return data.result
  }
  throw new Error(data?.message?.message ?? "Failed to load scanner files")
}

/** Download a pre-signed file URL — same blob → File pattern as `downloadScannerAttachment`. */
export const downloadScannerFileFromUrlApi = async (
  url: string,
  fileName: string,
  scannerName: string,
  signal?: AbortSignal,
): Promise<File> => {
  // Pre-signed CloudFront/S3 URLs are self-authenticating via query params.
  // Using a bare axios instance avoids sending custom headers (Scanner, Authorization)
  // that trigger CORS preflight failures on the CDN.
  const res = await axios.get<Blob>(url, { responseType: "blob", signal })
  return new File([res.data], fileName)
}
